import type { ItemFieldRow } from './item-field-reader';
import type { FormulaReadDependencies } from './formula-read-context';
import { createFormulaReadContext } from './formula-read-context';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath, removeIntegerCompilePath } from './integer-compute-slots';
import { md5PhpJson } from './php-json-serialize';
import { phpJsonString, phpJsonTruthy } from './php-json-values';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const has = (v: unknown, k: string): boolean => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k);
const own = (v: unknown, k: string): unknown => has(v, k) ? (v as Row)[k] : undefined;
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce(own, v);
const present = (v: unknown): boolean => v !== null && v !== undefined;
const requireArray = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Rollup cache requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Rollup cache ID requires a scalar string'); return phpJsonString(v); };
export interface RollupCacheMetadata {
    volatileKeys: readonly string[];
    damagePercentKeys: readonly string[];
    integerLayers: readonly string[];
    sensesNames: readonly string[];
    /** Restore deterministic PHP producer types before hashing raw browser compiles. */
    signatureValue?: (payload: ItemFieldRow) => unknown;
}
/** Cache helpers preserve the retained JSON boundary. A signature here cannot
 * recover PHP double/int distinctions erased before transport; do not use it
 * as proof that a native PHP carry is compatible.
 */
export function createRollupCacheContext(deps: FormulaReadDependencies & RollupCacheMetadata, initialCarry: ItemFieldRow = {}) {
    const readers = createFormulaReadContext(deps);
    const carry = new Map(Object.entries(integerCompileWorkingRow(requireArray(initialCarry))));
    const copy = <T>(v: T): T => arrayLike(v) ? integerCompileResult(integerCompileWorkingRow(v)) as T : v;
    function stripVolatile(v: unknown): unknown {
        if (!arrayLike(v)) return v;
        return copy(Object.fromEntries(Object.entries(v).filter(([key]) => !deps.volatileKeys.includes(key)).map(([key, child]) => [key, stripVolatile(child)])));
    }
    function payload(input: ItemFieldRow): ItemFieldRow {
        requireArray(input);
        return copy({ilt: own(input, 'item_layer_types') ?? [], st: own(input, 'statuses') ?? [], inv: stripVolatile(own(input, 'inventory') ?? []),
            dmg: deps.damagePercentKeys.map(key => own(input, key) ?? null), wa: path(input, 'encounter_creatures', 'weapon_attack') ?? null});
    }
    function key(input: ItemFieldRow): string {
        requireArray(input); const id = path(input, 'encounter_creatures', 'encounter_creature_id');
        return typeof id === 'string' && id !== '' ? id : phpJsonString(readers.itemField(input, 'item_id') ?? '');
    }
    const consumed = deps.integerLayers.filter(name => name !== 'teleport' && !name.startsWith('speed_') && name !== 'reach' && name !== 'extra_attack' && !deps.sensesNames.includes(name));
    function reproduceUnsets(input: ItemFieldRow): ItemFieldRow {
        const row = integerCompileWorkingRow(requireArray(input));
        for (const name of consumed) delete row[name];
        return integerCompileResult(row);
    }
    function snapshot(input: ItemFieldRow): ItemFieldRow {
        requireArray(input); const ec = phpJsonTruthy(path(input, 'encounter_creatures', 'encounter_creature_id')), out: Row = {};
        for (const bucket of ['modifiers', 'modifier_sources', 'damage_mitigation', 'essences']) {
            const value = path(input, 'computed', 'creature', bucket); if (present(value)) out[bucket] = value;
        }
        // the damage_mitigation host memo (ft_compute_damage_mitigation_chokepoint, 2026-10-07) rides with the carry
        const dmMemo = path(input, 'computed', '_cache', 'dm_host_fragments'); if (present(dmMemo)) out.dm_host_fragments = dmMemo;
        const combat: Row = {};
        for (const leaf of ['extra_attack', 'hindered_attack_counter']) {
            const value = path(input, 'computed', 'creature', 'combat', leaf); if (present(value)) combat[leaf] = value;
        }
        out.combat = combat;
        out.dmgpct = Object.fromEntries(deps.damagePercentKeys.filter(name => has(input, name)).map(name => [name, own(input, name)]));
        if (!ec && present(path(input, 'computed', 'vitality'))) out.vitality = path(input, 'computed', 'vitality');
        if (ec && present(path(input, 'encounter_creatures', 'weapon_attack'))) out.weapon_attack = path(input, 'encounter_creatures', 'weapon_attack');
        return copy(out);
    }
    function restore(input: ItemFieldRow, output: ItemFieldRow): ItemFieldRow {
        const row = integerCompileWorkingRow(requireArray(input)); requireArray(output);
        const ec = phpJsonTruthy(path(row, 'encounter_creatures', 'encounter_creature_id'));
        for (const bucket of ['modifiers', 'modifier_sources', 'damage_mitigation', 'essences']) {
            const target = ['computed', 'creature', bucket];
            if (has(output, bucket)) putIntegerCompilePath(row, target, copy(own(output, bucket))); else removeIntegerCompilePath(row, target);
        }
        const dmTarget = ['computed', '_cache', 'dm_host_fragments'];
        if (has(output, 'dm_host_fragments')) putIntegerCompilePath(row, dmTarget, copy(own(output, 'dm_host_fragments'))); else removeIntegerCompilePath(row, dmTarget);
        const combat = arrayLike(own(output, 'combat')) ? own(output, 'combat') : [];
        for (const leaf of ['extra_attack', 'hindered_attack_counter']) {
            const target = ['computed', 'creature', 'combat', leaf];
            if (has(combat, leaf)) putIntegerCompilePath(row, target, copy(own(combat, leaf))); else removeIntegerCompilePath(row, target);
        }
        const percentages = arrayLike(own(output, 'dmgpct')) ? own(output, 'dmgpct') : [];
        for (const name of deps.damagePercentKeys) {
            if (has(percentages, name)) row[name] = copy(own(percentages, name)); else delete row[name];
        }
        if (!ec && has(output, 'vitality')) putIntegerCompilePath(row, ['computed', 'vitality'], copy(own(output, 'vitality')));
        if (ec && has(output, 'weapon_attack')) putIntegerCompilePath(row, ['encounter_creatures', 'weapon_attack'], copy(own(output, 'weapon_attack')));
        return integerCompileResult(row);
    }
    return {stripVolatile, payload, key, snapshot, restore, reproduceUnsets, consumed: () => [...consumed],
        signature: (input: ItemFieldRow): string => md5PhpJson(deps.signatureValue ? deps.signatureValue(payload(input)) : payload(input)),
        setCarry: (rawId: string, rawSignature: string, output: ItemFieldRow): void => {
            const id = stringArg(rawId), sig = stringArg(rawSignature); requireArray(output);
            if (id !== '') carry.set(id, copy({sig, out: output}));
        },
        takeCarry: (rawId: string): ItemFieldRow | null => {
            const id = stringArg(rawId), value = carry.get(id);
            if (id === '' || !present(value)) return null;
            carry.delete(id); return arrayLike(value) ? copy(value) : null;
        },
    };
}
